import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const RESUBSCRIBE_THROTTLE_MS = 24 * 60 * 60 * 1000; // once a day is plenty
const LAST_SUBSCRIBED_KEY = 'web_push_last_subscribed_at';

const isNativePlatform = () => !!(window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform());

const isSupported = () =>
  !isNativePlatform() &&
  'serviceWorker' in navigator &&
  'PushManager' in window &&
  'Notification' in window;

const urlBase64ToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map((c) => c.charCodeAt(0)));
};

/**
 * Subscribes this browser (reusing an existing subscription) and registers it
 * with the backend for the signed-in user, then stamps `throttleKey`.
 * Shared by the admin and customer flows; callers own permission handling.
 * Throws on failure — every caller wraps it, since push is best-effort.
 */
const registerSubscription = async (throttleKey) => {
  const { data } = await axios.get(`${API}/notifications/vapid-key`);
  if (!data?.publicKey) return false;

  const registration = await navigator.serviceWorker.ready;
  let subscription = await registration.pushManager.getSubscription();
  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(data.publicKey),
    });
  }

  await axios.post(`${API}/notifications/subscribe`, subscription.toJSON());
  // The subscription is registered at this point; a blocked-storage failure
  // here only costs the throttle, so it must not report the whole thing failed.
  try { localStorage.setItem(throttleKey, String(Date.now())); } catch { /* storage blocked */ }
  return true;
};

/**
 * Ensures the current browser has a live Web Push subscription registered on
 * the backend. This is the only OS-push delivery path for the admin panel,
 * which (unlike the Customer/Rider apps) has no native build and therefore
 * never gets an FCM token. No-ops silently on native apps, unsupported
 * browsers, or when the user has denied/not yet granted permission.
 */
export const ensureWebPushSubscribed = async () => {
  if (!isSupported()) return;
  if (Notification.permission === 'denied') return;

  const lastSubscribed = Number(localStorage.getItem(LAST_SUBSCRIBED_KEY) || 0);
  const alreadyGranted = Notification.permission === 'granted';
  if (alreadyGranted && Date.now() - lastSubscribed < RESUBSCRIBE_THROTTLE_MS) return;

  try {
    if (Notification.permission === 'default') {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') return;
    }
    await registerSubscription(LAST_SUBSCRIBED_KEY);
  } catch (error) {
    // Silent — push is a best-effort enhancement, never block the admin UI on it.
  }
};

// ---------------------------------------------------------------- customers --
//
// Customers on the website had no OS-level channel at all: only the admin
// panel ever subscribed, so order updates sent to a web customer went nowhere.
// Unlike the admin flow, customers are never prompted on page load — the
// browser prompt appears only from an explicit "Enable" tap (a user gesture,
// which Safari and Firefox require), and the in-app offer is shown at most
// once per OFFER_COOLDOWN_MS so a customer who ignores it isn't nagged.

const CUSTOMER_OFFERED_KEY = 'web_push_customer_offered_at';
const OFFER_COOLDOWN_MS = 30 * 24 * 60 * 60 * 1000;

// Keyed per user, so a browser shared across accounts (or a role switch) still
// registers the subscription under whoever is signed in now.
const customerThrottleKey = (userId) => `${LAST_SUBSCRIBED_KEY}:${userId}`;

const readTimestamp = (key) => {
  try { return Number(localStorage.getItem(key) || 0); } catch { return 0; }
};

/** Silently keeps an already-granted customer registered. Never prompts. */
export const syncCustomerWebPush = async (userId) => {
  if (!userId || !isSupported() || Notification.permission !== 'granted') return;
  const key = customerThrottleKey(userId);
  if (Date.now() - readTimestamp(key) < RESUBSCRIBE_THROTTLE_MS) return;
  try { await registerSubscription(key); } catch { /* best-effort */ }
};

/** Whether the one-time in-app offer should be shown now. */
export const shouldOfferCustomerWebPush = () =>
  isSupported() &&
  Notification.permission === 'default' &&
  Date.now() - readTimestamp(CUSTOMER_OFFERED_KEY) >= OFFER_COOLDOWN_MS;

export const markCustomerWebPushOffered = () => {
  try { localStorage.setItem(CUSTOMER_OFFERED_KEY, String(Date.now())); } catch { /* storage blocked */ }
};

/**
 * Asks for permission and registers. MUST be called directly from a click
 * handler: everything before `requestPermission()` is synchronous so the call
 * stays inside the browser's user-activation window. Resolves true only once
 * the subscription is registered; never throws.
 */
export const enableCustomerWebPush = async (userId) => {
  if (!userId || !isSupported()) return false;
  markCustomerWebPushOffered();
  try {
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') return false;
    return await registerSubscription(customerThrottleKey(userId));
  } catch {
    return false;
  }
};
